<script setup lang="ts">
import { ref } from 'vue';
import { getLabel } from '@/utils/data-display-utils';

import BaseDialog from '@/components/base/BaseDialog.vue';
import termsQuestion from '../questions/termsQuestion.vue';
import propositionQuestions from '@/components/questions/propositionQuestions.vue';

import { showToaster } from '@/utils/toaster-util';
import { Locales } from '@/store/language.interface';

// PROPS
const props = defineProps<{
  sectionId: number;
  languageEnabled?: Locales[];
}>();

// REFS
const propositionDialogRef = ref<InstanceType<typeof BaseDialog>>();
const activeScreen = ref<string>('terms');
const acceptConditions = ref<boolean>(false);

// FUNCTIONS
function openDialog() {
  propositionDialogRef.value?.openDialog();
}

function closeDialog() {
  activeScreen.value = 'terms';
  propositionDialogRef.value?.closeDialog();
  resetDialog();
}

function resetDialog() {
  acceptConditions.value = false;
}

function updateView(value) {
  activeScreen.value = value;
  if (activeScreen.value === 'moderation') {
    showToaster({
      message: getLabel('dialog.moderation.message.success-with-moderation', props.languageEnabled, {
        named: { type: getLabel('dialog.moderation.types.proposition', props.languageEnabled) },
      }),
      type: 'positive',
    });
    closeDialog();
  }
}

function updateConditions(value) {
  acceptConditions.value = value;
}

defineExpose({
  openDialog,
  closeDialog,
});
</script>

<template>
  <BaseDialog class="proposition" ref="propositionDialogRef">
    <template v-slot:title>
      <div class="proposition-title">
        {{ getLabel('dialog.proposition.title', props.languageEnabled) }}
      </div>
    </template>
    <template v-slot:content>
      <Form class="proposition-data">
        <div class="proposition-data__conditions" v-if="activeScreen === 'terms'">
          <terms-question
            :accept-conditions="acceptConditions"
            :language-enabled="props.languageEnabled"
            @cancel="closeDialog"
            @update="updateView($event)"
            @conditions="updateConditions"
          />
        </div>
        <div class="proposition-data__proposition" v-if="activeScreen === 'proposition'">
          <proposition-questions
            :section-id="props.sectionId"
            :language-enabled="props.languageEnabled"
            @close-dialog="closeDialog"
            @update="updateView($event)"
          />
        </div>
      </Form>
    </template>
  </BaseDialog>
</template>

<style lang="scss">
.proposition {
  &-title {
    font-size: $small-title;
  }

  &-data {
    &__conditions {
      display: flex;
      flex-direction: column;
      gap: $space-sm;
    }
  }
}
</style>
